import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Send, Gift, Lock, Trash2, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import { GiftPicker } from "@/components/GiftPickerV2";
import { CallScreen } from "@/components/CallScreen";
import { notify } from "@/hooks/useSounds";
import { fxMessageSent, fxMessageReceived } from "@/utils/gamefx";
import { AdeLoadingSpinner } from "@/components/AdeLoadingSpinner";
import { t } from "@/i18n";
import { E2ERestoreBanner } from "@/components/E2ERestoreBanner";
import { ensureKeyPair, deriveSharedKey, encryptText, decryptText } from "@/utils/e2e";

export default function Chat() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [sharedKey, setSharedKey] = useState(null);
  const [decrypted, setDecrypted] = useState({});
  const [needsRestore, setNeedsRestore] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [confirmEveryone, setConfirmEveryone] = useState(false);
  const [showCall, setShowCall] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-start call when arriving from the Calls section (?call=1)
  useEffect(() => {
    if (searchParams.get("call") === "1") {
      setSearchParams({}, { replace: true });
      setTimeout(() => setShowCall(true), 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [callHistory, setCallHistory] = useState([]);
  const [e2eError, setE2eError] = useState(false);
  const decryptedRef = useRef({});
  
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  const fetchMessages = async () => {
    try {
      const [chatResponse, meResponse, callsResponse] = await Promise.all([
        api.get(`/chat/${userId}`),
        api.get('/auth/me'),
        api.get(`/calls/history/${userId}`).catch(() => ({ data: { calls: [] } }))
      ]);
      
      const newMessages = chatResponse.data.messages;
      
      // Play sound if new message received (not sent by me)
      if (newMessages.length > lastMessageCount && lastMessageCount > 0) {
        const lastMsg = newMessages[newMessages.length - 1];
        if (lastMsg && lastMsg.from_user_id !== meResponse.data.user_id) {
          fxMessageReceived(); // Suono soft + haptic
          notify('message');
        }
      }
      
      setLastMessageCount(newMessages.length);
      setMessages(newMessages);
      setCallHistory(callsResponse.data.calls || []);
      setOtherUser(chatResponse.data.user);
      setCurrentUserId(meResponse.data.user_id);
    } catch (error) {
      if (error.response?.status === 403) {
        navigate(-1);
      }
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [userId]);
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // E2E setup: generate my keypair and derive shared key with the other user
  useEffect(() => {
    const setupE2E = async () => {
      if (!currentUserId || !otherUser?.user_id) return;
      try {
        const kp = await ensureKeyPair(currentUserId);
        if (!kp) {
          setNeedsRestore(true);
          return;
        }
        if (otherUser.e2e_public_key) {
          try {
            const key = await deriveSharedKey(kp.priv, otherUser.e2e_public_key);
            setSharedKey(key);
            setE2eError(false);
          } catch (err) {
            console.error("E2E derive failed (invalid peer key)", err);
            setE2eError(true);
          }
        }
      } catch (e) {
        console.error("E2E setup failed", e);
      }
    };
    setupE2E();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, otherUser?.user_id, otherUser?.e2e_public_key]);
  
  // Decrypt incoming encrypted messages
  useEffect(() => {
    if (!sharedKey) return;
    const run = async () => {
      let changed = false;
      for (const m of messages) {
        if (m.encrypted && m.iv && decryptedRef.current[m.message_id] === undefined) {
          decryptedRef.current[m.message_id] = await decryptText(sharedKey, m.content, m.iv);
          changed = true;
        }
      }
      if (changed) setDecrypted({ ...decryptedRef.current });
    };
    run();
  }, [messages, sharedKey]);
  
  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    const messageContent = newMessage.trim();
    setNewMessage("");
    
    const tempMessage = {
      message_id: `temp_${Date.now()}`,
      from_user_id: currentUserId,
      to_user_id: userId,
      content: messageContent,
      created_at: new Date().toISOString(),
      read: false
    };
    setMessages(prev => [...prev, tempMessage]);
    fxMessageSent();
    
    try {
      let payload = { to_user_id: userId, content: messageContent };
      if (sharedKey) {
        const enc = await encryptText(sharedKey, messageContent);
        payload = { to_user_id: userId, content: enc.content, iv: enc.iv, encrypted: true };
      }
      const res = await api.post(`/chat/${userId}`, payload);
      if (payload.encrypted && res.data?.message_id) {
        decryptedRef.current[res.data.message_id] = messageContent;
        setDecrypted({ ...decryptedRef.current });
      }
      // Replace optimistic temp id with the real server id (needed for deletion)
      if (res.data?.message_id) {
        setMessages(prev => prev.map(m => m.message_id === tempMessage.message_id
          ? { ...m, message_id: res.data.message_id, content: payload.encrypted ? payload.content : m.content, iv: payload.iv || null, encrypted: !!payload.encrypted, created_at: res.data.created_at || m.created_at }
          : m));
      }
    } catch (error) {
      setMessages(prev => prev.filter(m => m.message_id !== tempMessage.message_id));
      setNewMessage(messageContent);
    } finally {
      setSending(false);
    }
  };
  
  const handleDeleteForMe = async (messageId) => {
    try {
      await api.delete(`/chat/message/${messageId}`);
      setMessages(prev => prev.filter(m => m.message_id !== messageId));
      setSelectedMsg(null);
      toast.success(t("messageDeleted"));
    } catch {
      toast.error(t("messageDeleteError"));
    }
  };
  
  const handleDeleteForEveryone = async (messageId) => {
    try {
      await api.delete(`/chat/message/${messageId}/everyone`);
      setMessages(prev => prev.map(m => m.message_id === messageId
        ? { ...m, deleted_for_everyone: true, content: "", encrypted: false, iv: null, story_ref: null }
        : m));
      setSelectedMsg(null);
      setConfirmEveryone(false);
      toast.success(t("messageDeleted"));
    } catch {
      toast.error(t("messageDeleteError"));
    }
  };
  
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };
  
  const getAvatarRingClass = () => {
    return otherUser?.is_verified ? 'ring-2 ring-offset-1 ring-[#D4A574]' : '';
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <AdeLoadingSpinner size="lg" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="chat-page">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 safe-top">
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 -ml-1.5 rounded-full hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
        </button>
        
        <button
          onClick={() => navigate(`/app/user/${userId}`)}
          className="flex items-center gap-2.5"
        >
          <Avatar className={`w-9 h-9 ${getAvatarRingClass()}`}>
            {otherUser?.picture ? (
              <AvatarImage 
                src={otherUser.picture.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${otherUser.picture}` : otherUser.picture}
                alt={otherUser.name}
              />
            ) : (
              <AvatarFallback className="bg-gray-100 text-gray-400 text-sm">
                {otherUser?.name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            )}
          </Avatar>
          <div className="flex flex-col items-start">
            <span className="text-sm font-medium text-gray-900">{otherUser?.name}</span>
            {sharedKey ? (
              <span className="flex items-center gap-1 text-[10px] text-emerald-600" data-testid="e2e-indicator">
                <Lock className="w-2.5 h-2.5" strokeWidth={2.5} />
                {t("e2eActive")}
              </span>
            ) : e2eError ? (
              <span className="flex items-center gap-1 text-[10px] text-amber-500" data-testid="e2e-unavailable">
                <Lock className="w-2.5 h-2.5" strokeWidth={2.5} />
                {t("e2eUnavailable")}
              </span>
            ) : null}
          </div>
        </button>
        
        <div className="flex-1"></div>
        
        {/* Audio call button */}
        <button
          onClick={() => setShowCall(true)}
          className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
          data-testid="chat-call-btn"
        >
          <Phone className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
        </button>
        
        {/* Gift button in chat header */}
        <button
          onClick={() => setShowGiftModal(true)}
          className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
          data-testid="chat-gift-btn"
        >
          <Gift className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
        </button>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 && callHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <p className="text-sm text-gray-400">{t("chatEmptyState")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {[...messages.map(m => ({ ...m, _type: 'msg' })), ...callHistory.slice(0, 5).map(c => ({ ...c, _type: 'call' }))]
              .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
              .map((item) => {
              if (item._type === 'call') {
                const failed = item.status === 'missed' || item.status === 'declined';
                const fmtDur = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
                const label = item.status === 'declined'
                  ? t("callDeclinedLog")
                  : item.status === 'missed'
                    ? t("callMissedLog")
                    : `${item.direction === 'outgoing' ? t("callOutgoingLog") : t("callIncomingLog")}${item.duration != null ? ` · ${fmtDur(item.duration)}` : ''}`;
                return (
                  <div key={item.call_id} className="flex justify-center py-1" data-testid={`call-log-${item.call_id}`}>
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] ${failed ? 'bg-red-50/60 border-red-100 text-red-400' : 'bg-gray-50 border-gray-100 text-gray-500'}`}>
                      <Phone className="w-3 h-3" strokeWidth={2} />
                      {label} · {formatTime(item.created_at)}
                    </div>
                  </div>
                );
              }
              const message = item;
              const isMine = message.from_user_id === currentUserId;
              
              return (
                <div 
                  key={message.message_id}
                  data-testid={`chat-message-${message.message_id}`}
                  className={`flex items-center gap-2 ${isMine ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    onClick={() => !message.deleted_for_everyone && !String(message.message_id).startsWith("temp_") && setSelectedMsg(selectedMsg === message.message_id ? null : message.message_id)}
                    data-testid={`message-bubble-${message.message_id}`}
                    className={`max-w-[75%] px-3.5 py-2 rounded-2xl cursor-pointer ${
                    isMine 
                      ? 'bg-gray-900 text-white rounded-br-md' 
                      : 'bg-gray-100 text-gray-900 rounded-bl-md'
                  }`}>
                    {message.story_ref && !message.deleted_for_everyone && (
                      <div className="mb-1.5 -mx-1 -mt-0.5" data-testid={`story-ref-${message.message_id}`}>
                        <p className={`text-[10px] mb-1 ${isMine ? 'text-gray-400' : 'text-gray-500'}`}>
                          {isMine ? t("storyReplyYou") : t("storyReplyToYours")}
                        </p>
                        {message.story_ref.media_url && (
                          <img
                            src={message.story_ref.media_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${message.story_ref.media_url}` : message.story_ref.media_url}
                            alt=""
                            className="w-20 h-32 object-cover rounded-xl border border-white/20"
                            loading="lazy"
                          />
                        )}
                      </div>
                    )}
                    {message.discussion_id && !message.deleted_for_everyone && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/app/discussion/${message.discussion_id}`); }}
                        className={`block text-left text-[10px] font-bold tracking-[0.18em] mb-1 ${isMine ? 'text-pink-300' : 'text-pink-500'}`}
                        data-testid={`shared-discussion-${message.message_id}`}>
                        DISCUSSION →
                      </button>
                    )}
                    {message.type === 'shared_post' && message.photo_id && !message.deleted_for_everyone && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/app?post=${message.photo_id}`); }}
                        className="block text-left mb-1"
                        data-testid={`shared-post-${message.message_id}`}>
                        <span className={`text-[10px] font-bold tracking-[0.18em] ${isMine ? 'text-orange-300' : 'text-orange-500'}`}>POST →</span>
                        {message.photo_url && (
                          <img
                            src={message.photo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${message.photo_url}` : message.photo_url}
                            alt=""
                            className="mt-1 w-28 h-28 object-cover rounded-xl border border-white/20"
                            loading="lazy"
                          />
                        )}
                      </button>
                    )}
                    {message.deleted_for_everyone ? (
                      <p className={`text-sm italic ${isMine ? 'text-gray-400' : 'text-gray-400'}`} data-testid={`tombstone-${message.message_id}`}>
                        {t("deletedTombstone")}
                      </p>
                    ) : (message.type === 'shared_post' && (!message.content || message.content.trim() === '[Shared Post]')) ? null : (
                    <p className="text-sm">
                      {message.encrypted
                        ? (decrypted[message.message_id] === undefined
                            ? <span className="inline-flex items-center gap-1 opacity-60"><Lock className="w-3 h-3" strokeWidth={2.4} />…</span>
                            : (decrypted[message.message_id] ?? t("notDecryptable")))
                        : message.content}
                    </p>
                    )}
                    <p className={`text-[10px] mt-0.5 flex items-center gap-1 ${isMine ? 'text-gray-400 justify-end' : 'text-gray-500'}`}>
                      {formatTime(message.created_at)}
                      {isMine && (
                        <span
                          className="inline-flex items-center gap-[3px] ml-0.5"
                          data-testid={`msg-status-${message.message_id}`}
                          title={message.read ? t("statusRead") : t("statusDelivered")}
                        >
                          <span className={`w-[5px] h-[5px] rounded-full ${message.read ? 'bg-pink-400' : 'bg-gray-500'}`} />
                          <span className={`w-[5px] h-[5px] rounded-full ${message.read ? 'bg-pink-400' : 'bg-gray-700'}`} />
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Message actions sheet: delete for me / for everyone (with confirmation) */}
      {selectedMsg && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 flex items-end"
          onClick={() => { setSelectedMsg(null); setConfirmEveryone(false); }}
          data-testid="msg-actions-overlay"
        >
          <div
            className="w-full bg-white rounded-t-3xl p-4 pb-8 space-y-2 safe-bottom"
            onClick={(e) => e.stopPropagation()}
          >
            {confirmEveryone ? (
              <>
                <p className="text-center text-sm font-medium text-gray-900 pb-1" data-testid="confirm-everyone-title">
                  Eliminare questo messaggio per tutti?
                </p>
                <p className="text-center text-xs text-gray-400 pb-2">Il contenuto verrà rimosso anche per l'altra persona.</p>
                <button
                  onClick={() => handleDeleteForEveryone(selectedMsg)}
                  className="w-full py-3 rounded-xl bg-red-500 text-white text-sm font-semibold active:scale-[0.98] transition-transform"
                  data-testid="confirm-delete-everyone-btn"
                >
                  {t("deleteForEveryone")}
                </button>
                <button
                  onClick={() => setConfirmEveryone(false)}
                  className="w-full py-3 rounded-xl text-gray-500 text-sm"
                  data-testid="cancel-confirm-everyone-btn"
                >
                  {t("cancelAction")}
                </button>
              </>
            ) : (
              <>
                <p className="text-center text-xs text-gray-400 pb-1">{t("deleteMessageTitle")}</p>
                <button
                  onClick={() => handleDeleteForMe(selectedMsg)}
                  className="w-full py-3 rounded-xl bg-gray-100 text-gray-900 text-sm font-medium active:scale-[0.98] transition-transform"
                  data-testid="delete-for-me-btn"
                >
                  {t("deleteForMe")}
                </button>
                <button
                  onClick={() => setConfirmEveryone(true)}
                  className="w-full py-3 rounded-xl bg-red-50 text-red-600 text-sm font-medium active:scale-[0.98] transition-transform"
                  data-testid="delete-for-everyone-btn"
                >
                  {t("deleteForEveryone")}
                </button>
                <button
                  onClick={() => setSelectedMsg(null)}
                  className="w-full py-3 rounded-xl text-gray-500 text-sm"
                  data-testid="cancel-delete-btn"
                >
                  {t("cancelAction")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* E2E Restore banner (new device) */}
      {needsRestore && currentUserId && (
        <E2ERestoreBanner
          userId={currentUserId}
          onRestored={async (kp) => {
            setNeedsRestore(false);
            if (otherUser?.e2e_public_key) {
              const key = await deriveSharedKey(kp.priv, otherUser.e2e_public_key);
              setSharedKey(key);
            }
          }}
        />
      )}
      
      {/* Input */}
      <div className="p-4 border-t border-gray-50 bg-white safe-bottom">
        <form onSubmit={handleSend} className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={t("chatPlaceholder")}
            className="flex-1 h-10 rounded-full border-gray-200 bg-gray-50 focus:bg-white px-4 text-sm"
            data-testid="chat-input"
          />
          <button
            type="submit"
            data-testid="chat-send-btn"
            disabled={!newMessage.trim() || sending}
            className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
      
      {/* Audio call screen */}
      {showCall && otherUser && (
        <CallScreen
          mode="outgoing"
          otherUser={otherUser}
          onClose={() => setShowCall(false)}
        />
      )}
      
      {/* Gift Picker — stesso design cinematico con anteprima reveal */}
      <GiftPicker
        isOpen={showGiftModal}
        onClose={() => setShowGiftModal(false)}
        recipientId={userId}
        recipientName={otherUser?.name}
      />
    </div>
  );
}
